import {StorageKeys, readJson, writeJson} from '../app/storage';

/**
 * The local record of a booking this phone made.
 *
 * It exists for one reason above all: the `manage_token` is served **once** by
 * the create call and never again, so the instant it arrives it is written here.
 * Lose it and the guest can no longer read or cancel their own booking except by
 * the link — which is why BookDone tells them to keep that link too.
 *
 * The display fields (`name`, `partySize`, `bookingTime`) are snapshots of what
 * the guest picked, kept so BookDone and BookManage can render the booking
 * immediately and correctly — including the wall time — without waiting on a
 * read and without a venue timezone the booking flow never has.
 */
export interface StoredBooking {
  tenantId: string;
  bookingId: string;
  manageToken: string;
  name: string;
  phone: string;
  partySize: number;
  /** `YYYY-MM-DDTHH:mm:00` wall time, exactly as sent. */
  bookingTime: string;
  reference?: string | null;
}

export function readBookings(): StoredBooking[] {
  const list = readJson<StoredBooking[]>(StorageKeys.bookings);
  return Array.isArray(list) ? list : [];
}

/** Newest first, de-duplicated by token and by booking id. Capped so it cannot grow forever. */
export function saveBooking(entry: StoredBooking): void {
  const rest = readBookings().filter(
    (b) => b.manageToken !== entry.manageToken && b.bookingId !== entry.bookingId,
  );
  writeJson(StorageKeys.bookings, [entry, ...rest].slice(0, 20));
}

export function findBookingByToken(token: string): StoredBooking | null {
  return readBookings().find((b) => b.manageToken === token) ?? null;
}

/** The most recent booking for a tenant — the fallback when BookManage has no token in its URL. */
export function latestBookingForTenant(tenantId: string): StoredBooking | null {
  return readBookings().find((b) => b.tenantId === tenantId) ?? null;
}
